/** Founders and teammates: their setup until it's complete, then their dashboard. One live stream for both. */
import { Navigate, Route, Routes } from 'react-router'
import type { Me } from '../../domain/schemas'
import { founderLanding } from '../../domain/route'
import { setupComplete } from '../../domain/setup'
import type { Api } from '../../infra/api'
import { Header } from '../components/Header'
import { Screen } from '../pages/Screen'
import { AccountPage } from './AccountPage'
import { ConnectionsPage } from './ConnectionsPage'
import { FounderProvider, useFounder } from './context'
import { Dashboard } from './Dashboard'
import { LearnManual } from './Learn'
import { SetupPage } from './SetupPage'
import { SharingPage } from './SharingPage'
import { UsagePage } from './UsagePage'
import { ErrorBoundary } from '../../ui/primitives/ErrorBoundary'
import './founder.css'

export function FounderApp({ api, me }: { api: Api; me: Me }) {
  return (
    <FounderProvider
      api={api}
      me={me}
      loading={<Screen title="One moment." quiet />}
      failed={(e, retry) => (
        <Screen
          title="We couldn't reach your computer's page."
          action={
            <button className="btn btn-soft" type="button" onClick={retry}>
              Try again
            </button>
          }
        >
          {e.message}
        </Screen>
      )}
    >
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/callback" element={<Landing />} />
        <Route path="/setup" element={<Shell where="Setup"><SetupPage /></Shell>} />
        <Route path="/setup/:step" element={<Shell where="Setup"><SetupPage /></Shell>} />
        <Route path="/home" element={<Shell where="Home" page><Dashboard /></Shell>} />
        <Route path="/connections" element={<Shell where="Home" page><ConnectionsPage /></Shell>} />
        <Route path="/account" element={<Shell where="Home" page><AccountPage /></Shell>} />
        <Route path="/sharing" element={<Shell where="Home"><SharingPage /></Shell>} />
        <Route path="/learn/*" element={<Shell where="Learn" page><FounderLearn /></Shell>} />
        <Route path="/usage" element={<Shell where="Home" page><FounderUsage /></Shell>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </FounderProvider>
  )
}

/** The manual, on the founder's own api. */
function FounderLearn() {
  return <LearnManual api={useFounder().api} />
}

/** Coding usage, on the founder's own api. */
function FounderUsage() {
  return <UsagePage api={useFounder().api} />
}

/** Decided once on arrival, from the server's record: person setup until it's complete, then the dashboard. */
function Landing() {
  const f = useFounder()
  return <Navigate to={founderLanding(setupComplete(f.setup))} replace />
}

const NAV = [
  { to: '/home', label: 'Home' },
  { to: '/connections', label: 'Connections' },
  { to: '/sharing', label: 'Sharing' },
  { to: '/learn', label: 'Learn' },
  { to: '/setup', label: 'Setup' },
  { to: '/account', label: 'Account' },
]

function Shell({ where, page, children }: { where: string; page?: boolean; children: React.ReactNode }) {
  return (
    <>
      <Header where={where} home="/" nav={NAV} />
      {/* Around the page only, so the header and its nav stay usable when a page breaks. */}
      {page ? <main className="page"><ErrorBoundary>{children}</ErrorBoundary></main> : <ErrorBoundary>{children}</ErrorBoundary>}
    </>
  )
}
